import { cn } from '@atlas/ui-primitives'
import type { LucideIcon } from 'lucide-react'
import { File as FileGlyph } from 'lucide-react'

/** Preview box sizes. `sm` is the list-row thumbnail; `lg` lines up with a drop panel. */
export const THUMBNAIL_SIZES = {
	sm: 'h-12 w-12',
	md: 'h-20 w-20',
	lg: 'h-32 w-32',
} as const

export type ThumbnailSize = keyof typeof THUMBNAIL_SIZES

export interface FileThumbnailProps {
	/** Alt text — the file's name. */
	name: string
	/** A generated or locally-previewed image. Absent means no thumbnail exists (yet, or ever). */
	thumbnailUrl?: string
	size?: ThumbnailSize
	/**
	 * Glyph for the file's type, from the caller's `fileTypeIcon(fileTypeToken(…))`.
	 * Resolved by the caller so this component stays free of the type-token table.
	 */
	icon?: LucideIcon
	/**
	 * Short type marker — `PDF`, `PSD`, `DOC`. Shown under the glyph so a file
	 * that will never have a thumbnail still says what it IS.
	 */
	marker?: string
	className?: string
}

/** Glyph sizing per box, so the placeholder scales with the preview it replaces. */
const GLYPH_SIZES: Record<ThumbnailSize, string> = {
	sm: 'h-5 w-5',
	md: 'h-7 w-7',
	lg: 'h-9 w-9',
}

/**
 * A file's preview: its thumbnail, or a typed placeholder when none exists.
 *
 * Not every file can have one, by design — the worker rasterizes images and
 * video, and documents are deliberately excluded. So the no-thumbnail case is a
 * permanent state for a large share of files, not a loading state, and it earns
 * a real answer: the type's glyph plus a short marker, rather than an anonymous
 * page icon that leaves a PDF and a PSD looking identical.
 */
export function FileThumbnail({
	name,
	thumbnailUrl,
	size = 'sm',
	icon: Icon = FileGlyph,
	marker,
	className,
}: FileThumbnailProps) {
	return (
		<span
			className={cn(
				'flex shrink-0 items-center justify-center overflow-hidden rounded-ds-sm border border-ds-border bg-ds-surface',
				THUMBNAIL_SIZES[size],
				className,
			)}
		>
			{thumbnailUrl ? (
				<img src={thumbnailUrl} alt={name} className="h-full w-full object-cover" />
			) : (
				<span className="flex flex-col items-center justify-center gap-0.5 text-ds-text-subtle">
					<Icon className={GLYPH_SIZES[size]} aria-hidden="true" />
					{/* Hidden at `sm`: the box is 48px, and a legible marker under a
					    20px glyph does not fit without crowding both. */}
					{marker && size !== 'sm' && <span className="font-ds-mono text-ds-xs uppercase leading-none">{marker}</span>}
				</span>
			)}
		</span>
	)
}
